import { useMemo, useCallback } from 'react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ColorDot } from '@/components/ui/color-dot'
import { Badge } from '@/components/ui/badge'
import type { Category } from '@/lib/hooks'

type CategorySelectProps = {
  categories: Category[]
  value: string | null
  onChange: (categoryId: string | null) => void
  showUncategorized?: boolean
  onPersonalCategorySelected?: () => void
}

export function CategorySelect({
  categories,
  value,
  onChange,
  showUncategorized = true,
  onPersonalCategorySelected,
}: CategorySelectProps) {
  // Find the parent of the currently selected value (to keep it visible even if hidden)
  const selectedCategory = useMemo(
    () => (value ? categories.find((c) => c.id === value) : undefined),
    [categories, value]
  )
  const selectedParentId = selectedCategory?.parent_id

  const parentCategories = useMemo(
    () =>
      categories
        .filter((c) => c.parent_id === null && (!c.is_hidden || c.id === selectedParentId))
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [categories, selectedParentId]
  )

  const hiddenParentIds = useMemo(
    () => new Set(categories.filter((c) => c.parent_id === null && c.is_hidden).map((c) => c.id)),
    [categories]
  )

  const childCategoriesByParent = useMemo(() => {
    const map: Record<string, Category[]> = {}
    for (const cat of categories) {
      if (cat.parent_id) {
        // Exclude children of hidden parents, unless this child is the selected value
        if (hiddenParentIds.has(cat.parent_id) && cat.id !== value) continue
        if (!map[cat.parent_id]) map[cat.parent_id] = []
        map[cat.parent_id].push(cat)
      }
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    }
    return map
  }, [categories, hiddenParentIds, value])

  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories]
  )

  const handleChange = useCallback(
    (v: string) => {
      const id = v === 'uncategorized' ? null : v
      onChange(id)
      if (id && onPersonalCategorySelected) {
        const cat = categoryMap.get(id)
        if (cat?.owner_id) {
          onPersonalCategorySelected()
        }
      }
    },
    [onChange, onPersonalCategorySelected, categoryMap]
  )

  return (
    <Select
      value={value || (showUncategorized ? 'uncategorized' : undefined)}
      onValueChange={handleChange}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder="カテゴリを選択" />
      </SelectTrigger>
      <SelectContent>
        {showUncategorized && (
          <>
            <SelectItem value="uncategorized">
              <ColorDot />
              未分類
            </SelectItem>
            <SelectSeparator />
          </>
        )}
        {parentCategories.map((parent, index) => {
          const children = childCategoriesByParent[parent.id] || []
          if (children.length === 0) return null
          return (
            <SelectGroup key={parent.id}>
              {index > 0 && <SelectSeparator />}
              <SelectLabel>{parent.name}</SelectLabel>
              {children.map((child) => (
                <SelectItem key={child.id} value={child.id}>
                  {parent.color && <ColorDot color={parent.color} />}
                  {child.name}
                  {child.owner_id && (
                    <Badge variant="outline" className="ml-1.5 text-[10px] py-0">個人</Badge>
                  )}
                </SelectItem>
              ))}
            </SelectGroup>
          )
        })}
      </SelectContent>
    </Select>
  )
}
